const BASE_PICKER_STYLES = ` html[data-picker-enabled="true"] .section-picker-hover { outline: 2px solid #2196F3 !important; outline-offset: -2px !important; cursor: pointer !important; }
  #section-picker-tooltip {
    position: fixed;
    z-index: 999999;
    pointer-events: none;
    background: #333;
    color: #fff;
    font-size: 12px;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    display: none;
  }
`;

const TAGGED_HIGHLIGHT_STYLES = `
  html[data-picker-enabled="true"] [data-updatable-tagged] {
    outline: 2px solid #4CAF50 !important;
    outline-offset: -2px !important;
    background-color: rgba(76, 175, 80, 0.08) !important;
  }
`;

/**
 * Locked elements are outlined whenever the picker is on, so a lock is never invisible state — a user
 * can see at a glance which copy the agent will leave alone. Listed after the tagged styles so a
 * locked element that is also a tagged slot reads as locked. Never applied outside the picker, so the
 * read-only preview and the exported/published HTML are unaffected.
 *
 * The second rule restores the hover outline on locked elements. Without it the dashed rule wins on
 * hover — same specificity, both `!important`, and this block is concatenated last — which killed the
 * hover affordance on exactly the elements a user must click in order to unlock them.
 */
const LOCKED_HIGHLIGHT_STYLES = `
  html[data-picker-enabled="true"] [data-locked],
  html[data-picker-enabled="true"] [do-not-modify] {
    outline: 2px dashed #757575 !important;
    outline-offset: -2px !important;
  }
  html[data-picker-enabled="true"] [data-locked].section-picker-hover,
  html[data-picker-enabled="true"] [do-not-modify].section-picker-hover {
    outline-style: solid !important;
    outline-color: #2196F3 !important;
  }
`;

/**
 * Applied when the host highlights a section from OUTSIDE the frame (hovering its row in the
 * editor's contents list). Deliberately the same treatment as a real hover, so pointing at the row
 * and pointing at the element read as the same thing. Needs no attribute gate: the class only ever
 * arrives via a message the email widget does not send.
 *
 * Every element this can reach is a tagged section, so the selector keys on `data-updatable-tagged`
 * to buy specificity (0,2,1) — a tie with the green tagged rule, which this block then wins by
 * being concatenated last. The leading `html` is what makes up the difference and is not optional:
 * without it the selector is (0,2,0) and loses on element count from anywhere in the stylesheet.
 * Scoping on the tag rather than on `data-picker-enabled` is what keeps the highlight working while
 * the picker toggle is off, which is the state the contents list is usually read in.
 */
const EXTERNAL_HOVER_STYLES = `
  html [data-updatable-tagged].section-picker-external-hover {
    outline: 2px solid #2196F3 !important;
    outline-offset: -2px !important;
  }
`;

/**
 * Identity-badge styling for the hovered element, applied only when the host turned labels on (see
 * `areElementLabelsEnabled`). The runtime pins the same element to the element's top-left instead of
 * the cursor, so the badge reads as a tag attached to the outline below it — hence the flat bottom
 * corners. Scoped to the attribute so the email widget's cursor tooltip keeps its original look.
 */
const ELEMENT_LABEL_STYLES = `
  html[data-picker-labels="true"] #section-picker-tooltip {
    background: #2196F3;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 11px;
    padding: 2px 6px;
    border-radius: 3px 3px 0 0;
  }
`;

/**
 * Wraps the user's raw HTML template with an injected style block and an external
 * script reference for the section picker functionality (hover highlights, click
 * handler, selector generator, postMessage communication).
 *
 * The script is loaded externally (not inline) to comply with the deployed CSP's
 * `script-src 'self'` directive, which blocks inline scripts in srcdoc iframes.
 *
 * Picker interaction is gated by `data-picker-enabled` on `<html>` — the parent
 * toggles this via a `TOGGLE_PICKER` postMessage.
 */
export function buildPickerSrcDoc(
  htmlContent: string,
  options: { hideTaggedHighlights?: boolean; scriptUrl: string },
): string {
  const styles =
    (options.hideTaggedHighlights
      ? BASE_PICKER_STYLES
      : BASE_PICKER_STYLES + TAGGED_HIGHLIGHT_STYLES) +
    LOCKED_HIGHLIGHT_STYLES +
    ELEMENT_LABEL_STYLES +
    EXTERNAL_HOVER_STYLES;
  const injection = `<style>${styles}</style><script src="${options.scriptUrl}"></script>`;

  const bodyCloseIndex = htmlContent.toLowerCase().lastIndexOf('</body>');
  if (bodyCloseIndex !== -1) {
    return htmlContent.slice(0, bodyCloseIndex) + injection + htmlContent.slice(bodyCloseIndex);
  }

  // Fallback: append to end
  return htmlContent + injection;
}
